import { ViewportScroller } from '@angular/common';
import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

@Directive({
    selector: '[appInterceptLinks], [intercept-links]',
    exportAs: 'interceptLinks',
    standalone: true
})
export class InterceptLinksDirective {
    private _excludedPathRegex: RegExp | undefined;
    private _dialogId?: string;

    /**
     * Support scroll in dialog/overlay
     * Pass in the dialog element id, if used to scroll to and anchor in dialog/overlay
     */
    @Input()
    set dialogId(id: string) {
        this._dialogId = id;
    }

    /**
     * Set the paths either by a string or a regex that should not be considered when parsing clicks
     */
    @Input()
    set exclude(paths: string | string[] | RegExp) {
        if (paths instanceof RegExp) {
            this._excludedPathRegex = paths;
        } else if (typeof paths === 'string') {
            this._excludedPathRegex = new RegExp(paths);
        } else {
            this._excludedPathRegex = new RegExp(paths.join('|'));
        }
    }

    constructor(private elementRef: ElementRef, private router: Router, private viewportScroller: ViewportScroller) {}

    @HostListener('click', ['$event'])
    onClick($event: MouseEvent): boolean {
        // Find the linkElement that was clicked
        let linkEle: HTMLAnchorElement = $event.target as any;

        // look up recursively until you find a linkElement
        // break when reaching container
        if (linkEle.tagName !== 'A') {
            while (linkEle.tagName !== 'A') {
                if (linkEle === this.elementRef.nativeElement) {
                    return true;
                }
                linkEle = linkEle.parentElement as any;
            }
        }

        // Eject if we lost the link element
        if (!linkEle) {
            return true;
        }

        // Eject if link has download attribute
        if (!!linkEle.download) {
            return true;
        }

        // Eject if link has a target thats not empty or top
        if (['', 'top', undefined].indexOf(linkEle.target) === -1) {
            return true;
        }

        this.navigate(linkEle.href);

        return false;
    }

    /**
     * Navigate to any internal url that is not excluded by the [exclude] input
     * @param path
     */
    navigate(path: string): void {
        const url = new URL(path);

        // Eject if link points to another domain
        if (url.host !== location.host) {
            return;
        }

        // Check for excluded paths
        if (this._excludedPathRegex && this._excludedPathRegex.test(url.pathname)) {
            return;
        }

        if (url.pathname === location.pathname && url.hash) {
            return;
        }

        const strippedUrlHash: string | undefined = url.hash ? url.hash.replace('#', '') : undefined;
        const extras: NavigationExtras = {
            fragment: strippedUrlHash,
            queryParams: this._parseQueryParams(url.search)
        };

        /**
         * If you are still here, the router will take over and do the navigation
         *
         * When anchor linking to an id the pathname will always be '/' because of "<base href="/">" in index.html.
         */
        if (url.hash && url.pathname === '/' && strippedUrlHash) {
            // If interceptLink is used in an overlay
            if (this._dialogId) {
                this._scrollToAnchorInDialog(strippedUrlHash);
                return;
            }

            // If it's the same hash, the scroll effect will not work, so use viewportScroller.scrollToAnchor instead
            if (url.hash === location.hash) {
                this.viewportScroller.scrollToAnchor(strippedUrlHash);
            } else {
                this.router.navigate([], extras);
            }
        } else {
            this.router.navigate([url.pathname], extras);
        }
    }

    private _scrollToAnchorInDialog(anchorId: string) {
        if (!this._dialogId) {
            return;
        }

        const dialogElement = document.getElementById(this._dialogId);
        if (dialogElement) {
            let idElement: HTMLElement | undefined;
            const goToId: HTMLElement | null = document.getElementById(anchorId);
            const goToName: NodeListOf<HTMLElement> = document.getElementsByName(anchorId);

            if (goToId) {
                idElement = goToId;
            } else if (goToName) {
                idElement = goToName[0];
            }

            if (idElement) {
                idElement.scrollIntoView(true);
            }
        }
    }

    private _parseQueryParams(searchString: string): { [key: string]: string } {
        return searchString
            .substring(1)
            .split('&')
            .reduce((params: any, pair: string) => {
                const kv = pair.split('=');
                params[kv[0]] = kv[1];
                return params;
            }, {});
    }
}
